import { useEffect, useState } from 'react';
import {
  pushState,
  isSubscribed,
  enablePush,
  disablePush,
  type PushState,
} from '../lib/push.ts';

/**
 * Notification control.
 *
 * Permission is requested only from this button. A prompt on page load
 * gets denied, and browsers make denial sticky -- the user then has to
 * dig into site settings to undo it, which nobody does.
 */
export const NotifyToggle = () => {
  const [state, setState] = useState<PushState>('default');
  const [on, setOn] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setState(pushState());
    void isSubscribed().then(setOn);
  }, []);

  const toggle = async () => {
    setBusy(true);
    try {
      if (on) {
        await disablePush();
        setOn(false);
      } else {
        const result = await enablePush();
        setOn(result === 'enabled');
        setState(pushState());
      }
    } finally {
      setBusy(false);
    }
  };

  // Nothing to offer: the shell already handles notifications and
  // there is no browser permission to grant.
  if (state === 'unsupported' || state === 'shell') return null;

  if (state === 'needs-install') {
    return (
      <span className="notifyNote">
        Install to your Home Screen to receive notifications
      </span>
    );
  }

  if (state === 'denied') {
    return (
      <span className="notifyNote">
        Notifications blocked in browser settings
      </span>
    );
  }

  return (
    <button onClick={toggle} disabled={busy} className="notifyBtn">
      {busy ? 'Working...' : on ? 'Notifications on' : 'Enable notifications'}
    </button>
  );
};
